/**
 * Ask Leynix — Command Palette
 * 
 * The Conversational Operating System for Aligned.
 * 
 * This is NOT a chat window. This is a command interface.
 * Triggered globally via Cmd/Ctrl+K from any screen.
 * 
 * Every response includes:
 * - Executive Answer
 * - Priority Actions (Action Cards)
 * - Supporting Evidence
 * - Reasoning
 * - Sources
 * - Follow-up Questions
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Command, 
  Search, 
  X, 
  Loader2, 
  ChevronRight, 
  ExternalLink,
  Clock,
  AlertTriangle,
  CheckCircle,
  Info,
  Sparkles,
  MessageSquare,
  FileText,
  Shield
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../lib/api";

// Priority badge colors
const priorityColors = {
  critical: "bg-red-500/10 text-red-600 border-red-500/30",
  high: "bg-orange-500/10 text-orange-600 border-orange-500/30",
  medium: "bg-yellow-500/10 text-yellow-700 border-yellow-500/30",
  low: "bg-blue-500/10 text-blue-600 border-blue-500/30"
};

// Priority icons
const PriorityIcon = ({ priority }) => {
  switch (priority) {
    case "critical": return <AlertTriangle className="w-3.5 h-3.5" />;
    case "high": return <AlertTriangle className="w-3.5 h-3.5" />;
    case "medium": return <Clock className="w-3.5 h-3.5" />;
    default: return <Info className="w-3.5 h-3.5" />;
  }
};

// Action Card Component
const ActionCard = ({ action, onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`p-3 rounded-lg border ${priorityColors[action.priority] || priorityColors.medium} cursor-pointer hover:scale-[1.01] transition-transform`}
    onClick={() => {
      if (action.url) {
        onNavigate(action.url);
      }
    }}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-start gap-2 flex-1 min-w-0">
        <PriorityIcon priority={action.priority} />
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm leading-tight">{action.title}</h4>
          <p className="text-xs opacity-80 mt-0.5 line-clamp-2">{action.description}</p>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-white/50">{action.priority}</span>
        {action.estimated_minutes && (
          <span className="text-[10px] opacity-70">{action.estimated_minutes} min</span>
        )}
      </div>
    </div>
    {action.why && (
      <div className="mt-2 pt-2 border-t border-current/10 text-xs opacity-70">
        <strong>Why:</strong> {action.why}
      </div>
    )}
    {action.url && (
      <div className="mt-2 flex items-center gap-1 text-xs font-medium">
        <span>Open</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    )}
  </motion.div>
);

// Evidence Card Component
const EvidenceCard = ({ evidence, onNavigate }) => (
  <div 
    className="p-2 rounded-md bg-secondary/50 border border-border/50 text-xs cursor-pointer hover:bg-secondary transition-colors"
    onClick={() => evidence.url && onNavigate(evidence.url)}
  >
    <div className="flex items-center gap-2">
      <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{evidence.title}</div>
        <div className="text-muted-foreground truncate">{evidence.summary}</div>
      </div>
      {evidence.status && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary border border-border capitalize shrink-0">
          {evidence.status.replace(/_/g, " ")}
        </span>
      )}
    </div>
  </div>
);

// Follow-up Suggestion Button
const FollowUpButton = ({ question, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-full border border-border bg-secondary/50 hover:bg-secondary transition-colors text-left"
  >
    <MessageSquare className="w-3 h-3 text-primary shrink-0" />
    <span className="truncate">{question}</span>
  </button>
);

// Confidence indicator
const ConfidenceIndicator = ({ confidence }) => {
  const pct = confidence?.percentage || 0;
  let color = "text-green-600";
  if (pct < 60) color = "text-yellow-600";
  if (pct < 40) color = "text-red-600";
  
  return (
    <div className="flex items-center gap-2 text-xs">
      <Shield className={`w-3.5 h-3.5 ${color}`} />
      <span className={color}>{pct}% confidence</span>
      {confidence?.reasoning && (
        <span className="text-muted-foreground truncate max-w-[200px]" title={confidence.reasoning}>
          — {confidence.reasoning}
        </span>
      )}
    </div>
  );
};

// Main Command Palette Component
export default function AskAlignedPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [response, setResponse] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      loadSuggestions();
    } else {
      // Reset state when closed
      setQuery("");
      setResponse(null);
      setShowHistory(false);
    }
  }, [isOpen]);

  // Load suggestions
  const loadSuggestions = async () => {
    try {
      const res = await api.get("/ask-aligned/suggestions");
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error("Failed to load suggestions:", err);
    }
  };

  // Load history
  const loadHistory = async () => {
    try {
      const res = await api.get("/ask-aligned/history?limit=10");
      setHistory(res.data || []);
    } catch (err) {
      console.error("Failed to load history:", err);
    }
  };

  // Submit query
  const submitQuery = useCallback(async (queryText) => {
    if (!queryText.trim()) return;
    
    setIsLoading(true);
    setResponse(null);
    
    try {
      const res = await api.post("/ask-aligned/query", {
        query: queryText,
        conversation_id: conversationId
      });
      
      setResponse(res.data);
      setConversationId(res.data.conversation_id);
      setQuery("");
      
      // Scroll to top of results
      if (resultsRef.current) {
        resultsRef.current.scrollTop = 0;
      }
    } catch (err) {
      console.error("Failed to submit query:", err);
      setResponse({
        answer: "I encountered an error processing your request. Please try again.",
        confidence: { percentage: 0, reasoning: "Error occurred" },
        actions: [],
        evidence: [],
        follow_ups: []
      });
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  // Handle navigation to action URLs
  const handleNavigate = (url) => {
    onClose();
    if (url.startsWith("http")) {
      window.open(url, "_blank");
    } else {
      window.location.href = url;
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitQuery(query);
    }
  };

  // Start new conversation
  const startNewConversation = () => {
    setConversationId(null);
    setResponse(null);
    setQuery("");
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-[10%] left-1/2 -translate-x-1/2 w-full max-w-2xl z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-secondary/30">
                <div className="flex items-center gap-2 text-primary">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-semibold">Ask Leynix</span>
                </div>
                <span className="text-xs text-muted-foreground">Conversational Operating System</span>
                <div className="flex-1" />
                {conversationId && (
                  <button
                    onClick={startNewConversation}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    New conversation
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-secondary transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {/* Search Input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                {isLoading ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                ) : (
                  <Search className="w-5 h-5 text-muted-foreground" />
                )}
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about your agency operations..."
                  className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
                  data-testid="ask-aligned-input"
                />
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <kbd className="px-1.5 py-0.5 rounded bg-secondary border border-border">↵</kbd>
                  <span>to ask</span>
                </div>
              </div>
              
              {/* Results Area */}
              <div 
                ref={resultsRef}
                className="max-h-[60vh] overflow-y-auto"
                data-testid="ask-aligned-results"
              >
                {/* Response */}
                {response && (
                  <div className="p-4 space-y-4">
                    {/* Executive Answer */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        <CheckCircle className="w-3.5 h-3.5 text-primary" />
                        Executive Answer
                      </div>
                      <p className="text-sm leading-relaxed">{response.answer}</p>
                    </div>
                    
                    {/* Reasoning */}
                    {response.reasoning_points?.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Why</div>
                        <ul className="space-y-1">
                          {response.reasoning_points.map((point, i) => (
                            <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Actions */}
                    {response.actions?.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Recommended Actions ({response.actions.length})
                        </div>
                        <div className="grid gap-2">
                          {response.actions.map((action, i) => (
                            <ActionCard key={i} action={action} onNavigate={handleNavigate} />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Evidence */}
                    {response.evidence?.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Supporting Evidence ({response.evidence.length})
                        </div>
                        <div className="grid gap-1.5">
                          {response.evidence.slice(0, 5).map((ev, i) => (
                            <EvidenceCard key={i} evidence={ev} onNavigate={handleNavigate} />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Sources */}
                    {response.sources?.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                        <span className="text-[10px] text-muted-foreground uppercase">Sources:</span>
                        {response.sources.map((src, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary border border-border">
                            {src.description} ({src.record_count})
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Confidence */}
                    {response.confidence && (
                      <div className="pt-2 border-t border-border">
                        <ConfidenceIndicator confidence={response.confidence} />
                      </div>
                    )}
                    
                    {/* Follow-ups */}
                    {response.follow_ups?.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-border">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Follow-up Questions
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {response.follow_ups.slice(0, 4).map((fu, i) => (
                            <FollowUpButton
                              key={i}
                              question={fu.question}
                              onClick={() => {
                                setQuery(fu.question);
                                inputRef.current?.focus();
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Suggestions (when no response) */}
                {!response && !isLoading && suggestions.length > 0 && (
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Suggested Questions
                      </div>
                      <button
                        onClick={() => {
                          setShowHistory(!showHistory);
                          if (!showHistory) loadHistory();
                        }}
                        className="text-xs text-primary hover:underline"
                      >
                        {showHistory ? "Show suggestions" : "View history"}
                      </button>
                    </div>
                    
                    {showHistory ? (
                      <div className="space-y-1">
                        {history.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No recent queries</p>
                        ) : (
                          history.map((item, i) => (
                            <button
                              key={i}
                              onClick={() => {
                                setQuery(item.query);
                                submitQuery(item.query);
                              }}
                              className="w-full text-left p-2 rounded-md hover:bg-secondary transition-colors group"
                            >
                              <div className="text-sm font-medium group-hover:text-primary transition-colors truncate">
                                {item.query}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {item.answer_preview}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {suggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setQuery(suggestion.query);
                              submitQuery(suggestion.query);
                            }}
                            className="w-full text-left p-2 rounded-md hover:bg-secondary transition-colors group flex items-center justify-between gap-2"
                            data-testid={`suggestion-${i}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium group-hover:text-primary transition-colors truncate">
                                {suggestion.query}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {suggestion.reason}
                              </div>
                            </div>
                            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border capitalize ${priorityColors[suggestion.priority] || ""}`}>
                              {suggestion.priority}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Loading state */}
                {isLoading && (
                  <div className="p-8 flex flex-col items-center justify-center gap-3">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    <p className="text-sm text-muted-foreground">Analyzing agency data...</p>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-4 py-2 border-t border-border bg-secondary/30 flex items-center justify-between text-[10px] text-muted-foreground">
                <div className="flex items-center gap-3">
                  <span><kbd className="px-1 py-0.5 rounded bg-secondary border border-border">⌘</kbd> + <kbd className="px-1 py-0.5 rounded bg-secondary border border-border">K</kbd> to toggle</span>
                  <span><kbd className="px-1 py-0.5 rounded bg-secondary border border-border">Esc</kbd> to close</span>
                </div>
                {response?.conversation_id && (
                  <span>Turn {response.turn_number} · {response.processing_time_ms}ms</span>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
